import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

const PdfToExcel = () => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	// Warn if user tries to close tab, refresh, or leave the site while conversion runs.
	useEffect(() => {
		if (!loading) return undefined;
		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [loading]);

	const fileSummary = useMemo(() => {
		if (!files.length) return "No files selected";
		const names = Array.from(files).map((f) => f.name).join(", ");
		const totalKb = Math.round(
			Array.from(files).reduce((acc, f) => acc + (f.size || 0), 0) / 1024
		);
		return `${files.length} PDF(s), ~${totalKb} KB total — ${names}`;
	}, [files]);

	const onPickFiles = (e) => {
		setError("");
		const list = Array.from(e.target.files || []);
		if (!list.length) {
			setFiles([]);
			return;
		}
		const nonPdf = list.filter((f) => !String(f.name || "").toLowerCase().endsWith(".pdf"));
		if (nonPdf.length) {
			setFiles([]);
			setError("Only PDF files (.pdf) are allowed.");
			return;
		}
		setFiles(list);
	};

	const downloadBlob = (blob, filename) => {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
	};

	const convert = async () => {
		if (!files.length) {
			setError("Select at least one PDF");
			return;
		}

		setLoading(true);
		setError("");
		try {
			const form = new FormData();
			for (let i = 0; i < files.length; i += 1) {
				form.append("files", files[i]);
			}

			const res = await pythonVmInstance.post("/reports/pdf-to-excel", form, {
				headers: { "Content-Type": "multipart/form-data" },
				responseType: "blob",
			});

			const contentDisposition = res.headers?.["content-disposition"] || "";
			const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
			const filename = match?.[1] || "pdf_extract_combined.xlsx";

			downloadBlob(res.data, filename);
		} catch (err) {
			let msg = err?.message || "Conversion failed";
			if (err?.response?.data instanceof Blob) {
				try {
					const text = await err.response.data.text();
					const parsed = JSON.parse(text);
					msg = parsed?.message || parsed?.error || text || msg;
				} catch {
					msg = "Conversion failed";
				}
			} else if (err?.response?.data) {
				msg =
					err.response.data?.message ||
					err.response.data?.error ||
					String(msg);
			}
			setError(String(msg));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h6" sx={{ mb: 0.5 }}>
				Line Sheet Convertor
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Upload one or more line sheet PDFs and download a combined Excel with product fields and images.
				Includes a source file column. Uses text embedded in the PDF.
			</Typography>

			<Paper sx={{ p: 2 }}>
				<Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
					<Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
						Choose PDF(s)
						<input
							type="file"
							hidden
							accept="application/pdf"
							multiple
							onChange={onPickFiles}
						/>
					</Button>

					<Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
						{fileSummary}
					</Typography>

					<Button
						variant="contained"
						onClick={convert}
						disabled={loading || !files.length}
						startIcon={
							loading ? <CircularProgress size={16} /> : <DownloadRoundedIcon />
						}
					>
						{loading ? "Converting…" : "Convert & Download"}
					</Button>
				</Stack>

				{loading ? (
					<Alert severity="warning" sx={{ mt: 2 }}>
						Conversion in progress — do not close this tab or refresh the page until the
						download finishes.
					</Alert>
				) : null}

				{error ? (
					<Typography color="error" sx={{ mt: 2 }}>
						{error}
					</Typography>
				) : null}
			</Paper>
		</Box>
	);
};

export default PdfToExcel;
